import { Box, Grid } from '@mui/material';
import { LineChart } from '@mui/x-charts/LineChart';
import { BarChart } from '@mui/x-charts/BarChart';
import MetricsGrid from './MetricsGrid';
import ChartCard from './ChartCard';
import ExportButton from './ExportButton';
import { AdminAnalyticsData } from '../../../types/admin';
import { formatNumber, formatCurrency, formatPercentage } from '../../../utils/formatters';
import PeopleIcon from '@mui/icons-material/People';
import HomeIcon from '@mui/icons-material/Home';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import BusinessIcon from '@mui/icons-material/Business';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';

interface OverviewTabProps {
  data: AdminAnalyticsData;
  loading?: boolean;
  onExport: () => void;
}

/**
 * OverviewTab - Overview metrics and key charts
 *
 * Displays:
 * - 8 key KPI cards (Total Users, DAU, WAU, MAU, Total Homes, Active Subscriptions, MRR, Churn)
 * - User growth trend chart (30 days)
 * - Home creation trend chart (30 days)
 */
export default function OverviewTab({ data, loading, onExport }: OverviewTabProps) {
  // Prepare metrics for cards
  const metrics = [
    {
      title: 'Total Users',
      value: formatNumber(data.overview.total_users),
      icon: <PeopleIcon />,
      color: '#1976d2',
      trend: {
        value: 8.5,
        label: 'vs last month',
        direction: 'up' as const,
      },
    },
    {
      title: 'Daily Active Users',
      value: formatNumber(data.overview.dau),
      subtitle: 'Last 24 hours',
      icon: <TrendingUpIcon />,
      color: '#2e7d32',
      trend: {
        value: 5.2,
        label: 'vs yesterday',
        direction: 'up' as const,
      },
    },
    {
      title: 'Weekly Active Users',
      value: formatNumber(data.overview.wau),
      subtitle: 'Last 7 days',
      icon: <TrendingUpIcon />,
      color: '#ed6c02',
      trend: {
        value: 3.8,
        label: 'vs last week',
        direction: 'up' as const,
      },
    },
    {
      title: 'Monthly Active Users',
      value: formatNumber(data.overview.mau),
      subtitle: 'Last 30 days',
      icon: <TrendingUpIcon />,
      color: '#9c27b0',
      trend: {
        value: 6.7,
        label: 'vs last month',
        direction: 'up' as const,
      },
    },
    {
      title: 'Total Homes',
      value: formatNumber(data.overview.total_homes),
      icon: <HomeIcon />,
      color: '#d32f2f',
      trend: {
        value: 4.3,
        label: 'vs last month',
        direction: 'up' as const,
      },
    },
    {
      title: 'Active Subscriptions',
      value: formatNumber(data.overview.active_subscriptions),
      subtitle: 'Pro + Agent tiers',
      icon: <AttachMoneyIcon />,
      color: '#0288d1',
      trend: {
        value: 7.2,
        label: 'vs last month',
        direction: 'up' as const,
      },
    },
    {
      title: 'Monthly Recurring Revenue',
      value: formatCurrency(data.overview.mrr, true),
      subtitle: 'Current MRR',
      icon: <AttachMoneyIcon />,
      color: '#2e7d32',
      trend: {
        value: 8.1,
        label: 'vs last month',
        direction: 'up' as const,
      },
    },
    {
      title: 'Churn Rate',
      value: formatPercentage(data.overview.churn_rate, 1),
      subtitle: 'Monthly churn',
      icon: <BusinessIcon />,
      color: '#d32f2f',
      trend: {
        value: -0.5,
        label: 'vs last month',
        direction: 'down' as const,
      },
    },
  ];

  // Prepare data for user growth chart (last 30 days)
  const last30DaysUserGrowth = data.userGrowth.slice(-30);
  const userGrowthDates = last30DaysUserGrowth.map((d) => new Date(d.date));
  const totalUsersData = last30DaysUserGrowth.map((d) => d.total_users);
  const dauData = last30DaysUserGrowth.map((d) => d.dau);
  const mauData = last30DaysUserGrowth.map((d) => d.mau);

  // Prepare data for home creation chart (last 30 days)
  const last30DaysHomeCreation = data.homeCreation.slice(-30);
  const homeCreationDates = last30DaysHomeCreation.map((d) => new Date(d.date));
  const homesCreatedData = last30DaysHomeCreation.map((d) => d.homes_created);

  return (
    <Box>
      {/* Top row: KPI cards */}
      <Box sx={{ mb: 4 }}>
        <MetricsGrid metrics={metrics} loading={loading} />
      </Box>

      {/* Charts */}
      <Grid container spacing={3}>
        {/* User Growth Chart */}
        <Grid item xs={12} lg={6}>
          <ChartCard
            title="User Growth Trend"
            subtitle="Total users, DAU, and MAU over last 30 days"
            loading={loading}
            action={<ExportButton onExport={onExport} />}
          >
            <LineChart
              xAxis={[
                {
                  data: userGrowthDates,
                  scaleType: 'time',
                  valueFormatter: (date) =>
                    new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
                },
              ]}
              series={[
                {
                  data: totalUsersData,
                  label: 'Total Users',
                  color: '#1976d2',
                  showMark: false,
                },
                {
                  data: mauData,
                  label: 'MAU',
                  color: '#9c27b0',
                  showMark: false,
                },
                {
                  data: dauData,
                  label: 'DAU',
                  color: '#2e7d32',
                  showMark: false,
                },
              ]}
              grid={{ vertical: true, horizontal: true }}
              sx={{
                '& .MuiLineElement-root': {
                  strokeWidth: 2,
                },
              }}
            />
          </ChartCard>
        </Grid>

        {/* Home Creation Chart */}
        <Grid item xs={12} lg={6}>
          <ChartCard
            title="Home Creation Trend"
            subtitle="New homes created per day over last 30 days"
            loading={loading}
          >
            <BarChart
              xAxis={[
                {
                  data: homeCreationDates,
                  scaleType: 'band',
                  valueFormatter: (date) =>
                    new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
                },
              ]}
              series={[
                {
                  data: homesCreatedData,
                  label: 'Homes Created',
                  color: '#d32f2f',
                },
              ]}
              grid={{ vertical: false, horizontal: true }}
            />
          </ChartCard>
        </Grid>
      </Grid>
    </Box>
  );
}
